/**
 * PlayerPerformanceCharts — three Recharts visualisations of a single
 * player's season. Computes everything client-side from data the
 * PlayerDetail page already fetches; no new round-trips needed.
 *
 *   1. Per-match contributions (last 10) — bars for goals+assists, line for minutes.
 *   2. Cumulative season trend — area chart of running goals + assists totals.
 *   3. Training attendance trend — line chart of rolling 5-session attendance % + raw dots.
 */
import { useMemo } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis,
  CartesianGrid, Tooltip, Legend, ResponsiveContainer, ComposedChart,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { TrendingUp, Activity, Calendar } from 'lucide-react';

const SHORT_DATE = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

export function PlayerPerformanceCharts({ matches = [], trainingStats = null }) {
  // ── Per-match dataset (oldest → newest, last 10) ──────────────────────
  const matchSeries = useMemo(() => {
    const played = (matches || [])
      .filter((m) => m.status === 'result' && (m.was_starter || m.was_sub))
      .slice() // copy
      .reverse() // backend returned newest-first; reverse to oldest-first
      .slice(-10);
    return played.map((m) => {
      const s = m.stats || {};
      return {
        label: SHORT_DATE(m.date),
        opponent: m.opponent || '',
        goals: Number(s.goals || 0),
        assists: Number(s.assists || 0),
        minutes: Number(s.minutes_played || 0),
        rating: s.rating != null ? Number(s.rating) : null,
      };
    });
  }, [matches]);

  // ── Cumulative season trend (chronological) ───────────────────────────
  const cumulativeSeries = useMemo(() => {
    const played = (matches || [])
      .filter((m) => m.status === 'result' && (m.was_starter || m.was_sub))
      .slice()
      .reverse(); // oldest first
    let gTotal = 0, aTotal = 0;
    return played.map((m) => {
      const s = m.stats || {};
      gTotal += Number(s.goals || 0);
      aTotal += Number(s.assists || 0);
      return {
        label: SHORT_DATE(m.date),
        goals: gTotal,
        assists: aTotal,
        ga: gTotal + aTotal, // goals + assists combined
      };
    });
  }, [matches]);

  // ── Training attendance dataset (chronological, last 12) ──────────────
  const trainingSeries = useMemo(() => {
    const sessions = (trainingStats?.sessions || []).slice(-12);
    if (!sessions.length) return [];

    // Rolling 5-session attendance percentage
    return sessions.map((s, i) => {
      const window = sessions.slice(Math.max(0, i - 4), i + 1);
      const presentCount = window.filter((w) => w.present).length;
      const rollingPct = Math.round((presentCount / window.length) * 100);
      return {
        label: SHORT_DATE(s.date),
        present: s.present ? 1 : 0,
        absent: s.present ? 0 : 1,
        rollingPct,
      };
    });
  }, [trainingStats]);

  const hasMatchData = matchSeries.length > 0;
  const hasTrainingData = trainingSeries.length > 0;
  if (!hasMatchData && !hasTrainingData) return null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="player-performance-charts">
      {hasMatchData && (
        <Card data-testid="player-chart-match-contributions">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="w-4 h-4 text-emerald-600" />
              Per-match contributions
            </CardTitle>
            <p className="text-xs text-gray-500">
              Last {matchSeries.length} {matchSeries.length === 1 ? 'match' : 'matches'} · goals + assists alongside minutes played
            </p>
          </CardHeader>
          <CardContent className="pt-2">
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <ComposedChart data={matchSeries} margin={{ top: 10, right: 10, bottom: 5, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} stroke="#9ca3af" allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#9ca3af" domain={[0, 90]} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    labelFormatter={(label, payload) => {
                      const opp = payload?.[0]?.payload?.opponent;
                      return opp ? `${label} vs ${opp}` : label;
                    }}
                    formatter={(val, name) => {
                      if (name === 'minutes') return [`${val} min`, 'Minutes'];
                      if (name === 'goals') return [val, 'Goals'];
                      if (name === 'assists') return [val, 'Assists'];
                      return [val, name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="goals"   fill="#059669" name="Goals"   radius={[3, 3, 0, 0]} maxBarSize={18} />
                  <Bar yAxisId="left" dataKey="assists" fill="#0ea5e9" name="Assists" radius={[3, 3, 0, 0]} maxBarSize={18} />
                  <Line yAxisId="right" type="monotone" dataKey="minutes" stroke="#f59e0b" strokeWidth={2} name="Minutes" dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {hasMatchData && cumulativeSeries.length > 1 && (
        <Card data-testid="player-chart-cumulative">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              Season trajectory
            </CardTitle>
            <p className="text-xs text-gray-500">
              Cumulative goals + assists across the season
            </p>
          </CardHeader>
          <CardContent className="pt-2">
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <AreaChart data={cumulativeSeries} margin={{ top: 10, right: 10, bottom: 5, left: -10 }}>
                  <defs>
                    <linearGradient id="goalsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#059669" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#059669" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="assistsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%"  stopColor="#0ea5e9" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Area type="monotone" dataKey="goals"   stroke="#059669" strokeWidth={2} fill="url(#goalsGradient)"   name="Goals (total)" />
                  <Area type="monotone" dataKey="assists" stroke="#0ea5e9" strokeWidth={2} fill="url(#assistsGradient)" name="Assists (total)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {hasTrainingData && (
        <Card data-testid="player-chart-training-attendance" className={cumulativeSeries.length > 1 ? '' : 'lg:col-span-2'}>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="w-4 h-4 text-amber-600" />
              Training attendance trend
            </CardTitle>
            <p className="text-xs text-gray-500">
              Last {trainingSeries.length} {trainingSeries.length === 1 ? 'session' : 'sessions'} · rolling 5-session attendance %
            </p>
          </CardHeader>
          <CardContent className="pt-2">
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <ComposedChart data={trainingSeries} margin={{ top: 10, right: 10, bottom: 5, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#9ca3af" />
                  <YAxis yAxisId="left"  tick={{ fontSize: 11 }} stroke="#9ca3af" domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
                  <YAxis yAxisId="right" orientation="right" hide domain={[0, 1]} />
                  <Tooltip
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                    formatter={(val, name) => {
                      if (name === 'rollingPct') return [`${val}%`, 'Rolling 5-session avg'];
                      if (name === 'present') return [val ? 'Yes' : 'No', 'Present'];
                      return [val, name];
                    }}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="right" dataKey="present" name="Present" fill="#059669" maxBarSize={8} radius={[2, 2, 0, 0]} />
                  <Bar yAxisId="right" dataKey="absent"  name="Absent"  fill="#fca5a5" maxBarSize={8} radius={[2, 2, 0, 0]} />
                  <Line yAxisId="left" type="monotone" dataKey="rollingPct" stroke="#f59e0b" strokeWidth={2.5} dot={{ r: 3, fill: '#f59e0b' }} name="Rolling 5-session avg" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
